/**
 * Tipos compartidos del frontend.
 * Reflejan el schema de D1 y las respuestas de la API.
 */

// ─── Clientes ───
export interface Client {
  id: string;
  name: string;
  business_name: string | null;
  email: string | null;
  phone: string | null;
  instagram: string | null;
  facebook: string | null;
  tiktok: string | null;
  website: string | null;
  plan_type: 'basico' | 'intermedio' | 'premium';
  monthly_fee: number;
  start_date: string | null;
  status: 'active' | 'paused' | 'inactive';
  credentials: string | null;
  marketing_plan: string | null;
  agreements: string | null;
  annual_objectives: string | null;
  google_drive_folder_id: string | null;
  avatar_color: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Computed fields from API
  pending_tasks?: number;
  monthly_hours_seconds?: number;
}

// ─── Tareas ───
export interface Task {
  id: string;
  client_id: string | null;
  title: string;
  description: string | null;
  priority: 0 | 1 | 2 | 3;
  status: 'pending' | 'in_progress' | 'completed';
  category: 'general' | 'personal' | 'client';
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  client_name?: string;
  avatar_color?: string;
}

// ─── Registro de Tiempo ───
export interface TimeEntry {
  id: string;
  client_id: string;
  task_id: string | null;
  description: string | null;
  start_time: string;
  end_time: string | null;
  duration_seconds: number;
  created_at: string;
  task_title?: string;
}

// ─── Finanzas ───
export interface Finance {
  id: string;
  type: 'income' | 'expense';
  category: string;
  description: string | null;
  amount: number;
  date: string;
  client_id: string | null;
  is_recurring: boolean;
  created_at: string;
  client_name?: string;
}

export interface FinanceSummary {
  month: string;
  income: { total: number; count: number };
  expense: { total: number; count: number };
  balance: number;
}

// ─── Métricas ───
export interface Metric {
  id: string;
  client_id: string;
  period: string;
  platform: string;
  followers: number;
  reach: number;
  impressions: number;
  engagement_rate: number;
  clicks: number;
  saves: number;
  shares: number;
  comments: number;
  likes: number;
  profile_visits: number;
  website_visits: number;
  leads: number;
  conversions: number;
  extra_data: string | null;
  ai_analysis: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Calendario de Contenidos ───
export interface ContentCalendarItem {
  id: string;
  client_id: string;
  title: string;
  content_type: 'post' | 'reel' | 'story' | 'carousel' | 'live' | 'blog' | 'ad';
  platform: string | null;
  scheduled_date: string;
  scheduled_time: string | null;
  caption: string | null;
  hashtags: string | null;
  visual_notes: string | null;
  status: 'planned' | 'in_production' | 'ready' | 'published';
  drive_file_id: string | null;
  period: string;
  created_at: string;
  updated_at: string;
}

// ─── Acciones de Marketing ───
export interface MarketingAction {
  id: string;
  client_id: string;
  period: string;
  action_type: 'campaign' | 'email' | 'ad' | 'collaboration' | 'event' | 'other';
  title: string;
  description: string | null;
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  due_date: string | null;
  results: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Producción (Kanban) ───
export interface ProductionItem {
  id: string;
  client_id: string;
  content_calendar_id: string | null;
  period: string;
  category: 'recording' | 'editing' | 'design';
  title: string;
  description: string | null;
  status: 'todo' | 'in_progress' | 'review' | 'done';
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── Copys ───
export interface ContentCopy {
  id: string;
  client_id: string;
  content_calendar_id: string | null;
  period: string;
  platform: string;
  copy_text: string;
  ai_generated: boolean;
  publication_status: 'draft' | 'approved' | 'scheduled' | 'published';
  published_at: string | null;
  published_url: string | null;
  created_at: string;
  updated_at: string;
  calendar_title?: string;
}

// ─── Cierre Mensual ───
export interface MonthlyClosing {
  id: string;
  client_id: string;
  period: string;
  closed_at: string;
  pdf_drive_file_id: string | null;
  drive_folder_id: string | null;
  summary: string | null;
}

// ─── Timer State ───
export interface TimerState {
  isRunning: boolean;
  clientId: string | null;
  clientName: string | null;
  taskId: string | null;
  startTime: number | null;
  elapsed: number; // seconds
  description: string;
}

// ─── Priority Labels ───
export const PRIORITY_LABELS: Record<number, string> = {
  0: 'Baja',
  1: 'Media',
  2: 'Alta',
  3: 'Urgente',
};

export const PRIORITY_COLORS: Record<number, string> = {
  0: 'text-muted-foreground',
  1: 'text-blue-500',
  2: 'text-amber-500',
  3: 'text-red-500',
};

// ─── Paquetes y Planes ───
export interface PackagePriceHistory {
  id: string;
  package_id: string;
  price: number;
  previous_price: number | null;
  reason: string | null;
  effective_date: string;
  created_at: string;
}

export interface PackagePlan {
  id: string;
  name: string;
  badge_color: string;
  description: string | null;
  features: string[];
  price: number;
  currency: string;
  is_active: number;
  created_at: string;
  updated_at: string;
  client_count?: number;
  clients?: Array<{
    id: string;
    name: string;
    business_name: string | null;
    avatar_color: string;
    monthly_fee: number;
  }>;
  last_price_change?: PackagePriceHistory | null;
  history?: PackagePriceHistory[];
}
